import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { gcApi } from 'config/axios';

/** redux selector */
import { selectGroup, selectGroupInvites, selectViewerSocialProfile } from 'reducers/group/groupPage';
import { selectGroupPageInviteKeyword, selectGroupPageInviteNextCursor } from 'reducers/group/groupPageInvite';

/** redux actions */
import {
    cancelInviteUserDone,
    inviteUserDone,
    searchUserDone,
    unblockUserDone,
    updateGroupInvites,
    updateGroupMembers,
    updateGroupUserConnections,
    updateSearchUserConnectionStatus,
    sortGroupMembers,
    setInviteErrorDialogState
} from '../actions/groupPageActions';
import { fetchMultipleUserSocialProfileImage } from 'actions/userSocialProfileImageActions';

import {
    GROUP_PAGE_CANCEL_INVITE_USER,
    GROUP_PAGE_INVITE_USER,
    GROUP_PAGE_SEARCH_USER,
    GROUP_PAGE_UNBLOCK_USER
} from '../actions/ActionTypes';

import { MIN_START_SEARCH_LENGTH } from 'utils/groups/GroupsUtil';

import '../actions/groupPageActionsTypeDefs';

export function callSearchUser({ keyword, start }) {
    const postData = new URLSearchParams();
    postData.append('keyword', keyword);
    postData.append('start', `${start + 1}`);
    postData.append('limit', `${15 + 1}`);
    return gcApi({
        method: 'post',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        url: '/usersearch-service/search?displayMutedStatus=true',
        data: postData
    });
}

export function* searchUserSaga({ reset }) {
    const group = yield select(selectGroup);
    const viewerSocialProfile = yield select(selectViewerSocialProfile);
    const keyword = yield select(selectGroupPageInviteKeyword);

    if (keyword.length < MIN_START_SEARCH_LENGTH) return;

    let start = yield select(selectGroupPageInviteNextCursor);
    if (reset) {
        start = 0;
    }
    try {
        let callList = [
            call(gcApi.get, `/group-service/group/${group.id}/members?supportOwner=true`),
            call(gcApi.get, `/group-service/group/${group.id}/invites`),
            call(
                gcApi.get,
                `/group-service/group/${group.id}/member/${viewerSocialProfile.profileId}/connections?supportOwner=true`
            ),
            call(gcApi.get, `/group-service/group/${group.id}/invites/rejected`)
        ];
        const [groupMembersRes, groupInvitesRes, groupUserConnectionsRes, groupRejectedInvitesRes] = yield all(
            callList
        );
        yield all([
            put(updateGroupMembers(groupMembersRes.data)),
            put(updateGroupInvites(groupInvitesRes.data, groupRejectedInvitesRes.data || [])),
            put(updateGroupUserConnections(groupUserConnectionsRes.data)),
            put(sortGroupMembers())
        ]);

        const userSearchRes = yield call(callSearchUser, { keyword, start });

        const list = userSearchRes.data.profileList.slice(0, 15);
        yield put(
            searchUserDone({
                reset,
                list,
                hasMore: userSearchRes.data.profileList.length > 15,
                nextCursor: start + 15,
                error: false
            })
        );
        yield fork(fetchSearchUserProfileImageDtoSaga, userSearchRes.data.profileList);
    } catch (error) {
        yield put(searchUserDone({ reset, error: true }));
    }
}

export function* unblockUserSaga({ connection }) {
    try {
        yield gcApi.delete(`/userblock-service/blockuser/${connection.displayName}`);
    } catch (error) {}
    const response = yield gcApi.get(`/connection-service/connection/status/${connection.displayName}`);
    yield put(updateSearchUserConnectionStatus(response.data[0]));
    yield put(unblockUserDone({ connection }));
}

export function* cancelInviteUserSaga({ connection }) {
    const group = yield select(selectGroup);
    const groupInvites = yield select(selectGroupInvites);
    const groupInvite = groupInvites.find((item) => item.inviteeId === connection.userId);
    if (!groupInvite) {
        yield put(cancelInviteUserDone({ connection }));
        return;
    }

    const { userGroupId, groupInviteId } = groupInvite;

    try {
        yield call(gcApi.delete, `/group-service/invite/group/${group.id}`, {
            data: {
                userGroupId,
                groupInviteId
            }
        });
        yield put(cancelInviteUserDone({ connection }));
    } catch (error) {
        yield put(cancelInviteUserDone({ connection, error: true }));
    }
}

export function* inviteUserSaga({ connection }) {
    const group = yield select(selectGroup);

    try {
        const response = yield call(gcApi.post, `/group-service/invite/group/${group.id}`, {
            userGroupId: group.id,
            inviteeId: connection.userId
        });
        yield put(inviteUserDone({ connection, error: false, newInvite: response.data }));
    } catch (error) {
        if (error.response && error.response.status === 429) {
            yield put(setInviteErrorDialogState({ open: true, errorType: 'limit' }));
        }
        yield put(inviteUserDone({ connection, error: true }));
    }
}

/**
 * handle fetch likers response. (use in opening liker dialog)
 * @param {{userId: number}[]} responseData
 */
function* fetchSearchUserProfileImageDtoSaga(responseData) {
    const userIds = (responseData || []).map((item) => item.userId);
    if (!userIds.length) {
        return;
    }
    yield put(fetchMultipleUserSocialProfileImage(userIds));
}

// watcher
export const groupPageInviteSagas = [
    takeLatest(GROUP_PAGE_SEARCH_USER, searchUserSaga),
    takeEvery(GROUP_PAGE_UNBLOCK_USER, unblockUserSaga),
    takeEvery(GROUP_PAGE_CANCEL_INVITE_USER, cancelInviteUserSaga),
    takeEvery(GROUP_PAGE_INVITE_USER, inviteUserSaga)
];
